import { createContext, useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';

import { DEFAULT_LABELS, ELECTRICAL_LABELS } from 'src/constants/recordFields';
import { PERFORMANCE_INDICATOR_LABELS } from 'src/constants/enpisFields';
import { LABELS } from 'src/constants/compressorFields';
import { LABELS as ENERGY_LABELS } from 'src/constants/energyFields';
import Unit from 'src/constants/unit';

const UnitContext = createContext({
  unit: Unit.METRIC,
  labels: {},
  electricalLabels: {},
  enpisLabels: {},
  compressorLabels: {},
  energyLabels: {},
  assignmentLabels: {},
  showUnitToggle: false,
  setShowUnitToggle: () => {},
  setUnit: () => {},
  setOffset: () => {},
  offset: {},
});

export function UnitProvider({ children }) {
  const { t } = useTranslation();
  const languageLabels = t('constants.recordFields', { returnObject: true });
  const assignmentLanguageLabels = t('views.assignmentsList.filters', { returnObject: true });
  const electricaLanguagelLabels = t('constants.electricalRecordFields', { returnObject: true });
  const enpisLanguageLabels = t('constants.enpisFields.labels', { returnObject: true });
  const compressorLanguageLabels = t('constants.compressorFields.labels', { returnObject: true });
  const energyLanguageLabels = t('constants.energyFields.labels', { returnObject: true });
  const [unit, setUnit] = useState({ value: Unit.METRIC, changed: false });
  const [showUnitToggle, setShowUnitToggle] = useState(false);
  const [labels, setLabels] = useState(languageLabels);
  const [electricalLabels, setElectricalLabels] = useState({ electricaLanguagelLabels });
  const [enpisLabels, setEnpisLabels] = useState({ enpisLanguageLabels });
  const [compressorLabels, setCompressorLabels] = useState({ compressorLanguageLabels });
  const [energyLabels, setEnergyLabels] = useState({ energyLanguageLabels });
  const [assignmentLabels, setAssignmentLabels] = useState({ assignmentLanguageLabels });
  const [offset, setOffset] = useState({});

  function handleUnitChange(newUnit) {
    setElectricalLabels(electricaLanguagelLabels);

    if (newUnit === Unit.METRIC) {
      setLabels(languageLabels);
      setEnpisLabels(enpisLanguageLabels);
      setCompressorLabels(compressorLanguageLabels);
      setEnergyLabels(energyLanguageLabels);
      setAssignmentLabels(assignmentLanguageLabels);
    } else {
      const languageImperialLabels = t('constants.IMPERIAL_LABELS', { returnObject: true });
      const enpisLanguageImperialLabels = t('constants.enpisFields.labelsImperial', { returnObject: true });
      const compressorLanguageImperialLabels = t('constants.compressorFields.labelsImperial', { returnObject: true });
      const energyLanguageImperialLabels = t('constants.energyFields.labelsImperial', { returnObject: true });

      const unitLabels = {};
      const enpisLabels = {};
      const compressorLabels = {};
      const energyLabels = {};

      const keys = Object.keys(DEFAULT_LABELS);
      const enpisKeys = Object.keys(PERFORMANCE_INDICATOR_LABELS);
      const compressorKeys = Object.keys(LABELS);
      const energyKeys = Object.keys(ENERGY_LABELS);

      const maxLength = Math.max(keys.length, enpisKeys.length, compressorKeys.length, energyKeys.length);

      for (let i = 0; i < maxLength; i++) {
        if (i < keys.length) {
          const key = keys[i];
          unitLabels[key] = newUnit === Unit.IMPERIAL && languageImperialLabels[key] ? languageImperialLabels[key] : languageLabels[key];
        }

        if (i < enpisKeys.length) {
          const enpisKey = enpisKeys[i];
          enpisLabels[enpisKey] =
            newUnit === Unit.IMPERIAL && enpisLanguageImperialLabels[enpisKey]
              ? enpisLanguageImperialLabels[enpisKey]
              : enpisLanguageLabels[enpisKey];
        }

        if (i < compressorKeys.length) {
          const compressorKey = compressorKeys[i];
          compressorLabels[compressorKey] =
            newUnit === Unit.IMPERIAL && compressorLanguageImperialLabels[compressorKey]
              ? compressorLanguageImperialLabels[compressorKey]
              : compressorLanguageLabels[compressorKey];
        }

        if (i < energyKeys.length) {
          const energyKey = energyKeys[i];
          energyLabels[energyKey] =
            newUnit === Unit.IMPERIAL && energyLanguageImperialLabels[energyKey]
              ? energyLanguageImperialLabels[energyKey]
              : energyLanguageLabels[energyKey];
        }
      }

      setLabels(unitLabels);
      setEnpisLabels(enpisLabels);
      setCompressorLabels(compressorLabels);
      setEnergyLabels(energyLabels);
      setElectricalLabels(electricalLabels);
      setAssignmentLabels(assignmentLanguageLabels);
    }

    setUnit({ value: newUnit, changed: true });
  }

  function handleOffset(key, value) {
    const update = { ...offset };
    update[key] = value;
    setOffset(update);
  }

  useEffect(() => {
    handleUnitChange(unit.value);
  }, [unit.value, t]);

  const contextValue = useMemo(() => ({
    unit: unit.value,
    unitChanged: unit.changed,
    labels,
    electricalLabels,
    enpisLabels,
    compressorLabels,
    energyLabels,
    assignmentLabels,
    setUnit: handleUnitChange,
    showUnitToggle,
    setShowUnitToggle,
    setOffset: handleOffset,
    offset,
  }));

  return <UnitContext.Provider value={contextValue}>{children}</UnitContext.Provider>;
}

UnitProvider.propTypes = {
  children: PropTypes.node,
};

export default UnitContext;
